/** CRM separators are product rules: French and Spanish also use dot grouping. */
export interface FormatNumberOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

export const getNumberSeparators = (language = "en") => {
  const code = language.toLowerCase().split(/[-_]/)[0];
  return ["it", "fr", "de", "es"].includes(code ?? "")
    ? { group: ".", decimal: "," }
    : { group: ",", decimal: "." };
};

/** Normalizes localized numeric strings before formatting them for display. */
export const parseNumberInput = (value: string | undefined, language = "en"): string => {
  const { group } = getNumberSeparators(language);
  const input = (value ?? "").replace(/\s/g, "");
  const dot = input.lastIndexOf(".");
  const comma = input.lastIndexOf(",");

  if (dot >= 0 && comma >= 0) {
    // With both separators, the final separator marks the decimal part.
    const decimal = dot > comma ? "." : ",";
    const grouping = decimal === "." ? "," : ".";
    return input.split(grouping).join("").replace(decimal, ".");
  }

  const separator = dot >= 0 ? "." : comma >= 0 ? "," : undefined;
  if (!separator) return input;

  // Preserve valid grouped integers; ambiguous "1.234" follows the UI locale.
  const groupedInteger = /^[+-]?[1-9]\d{0,2}([.,])\d{3}(?:\1\d{3})*$/;
  if (groupedInteger.test(input) && (separator === group || input.split(separator).length > 2)) {
    return input.split(separator).join("");
  }

  return input.replace(separator, ".");
};

/** Formats raw numbers/numeric API strings; missing or invalid values render as "--".
 * Pass the active UI language. Defaults preserve up to 20 decimal places;
 * set both fraction options to the same value for fixed precision.
 */
export const formatNumber = (
  value: number | string | null | undefined,
  language = "en",
  options: FormatNumberOptions = {},
): string => {
  if (value === null || value === undefined || (typeof value === "string" && !value.trim())) {
    return "--";
  }

  const numericValue = Number(value);
  // API dot-decimal strings remain canonical; accept comma-decimal strings as well.
  const number = typeof value === "string" && !Number.isFinite(numericValue)
    ? Number(parseNumberInput(value, language))
    : numericValue;
  if (!Number.isFinite(number)) return "--";

  const locale = getNumberSeparators(language).decimal === "," ? "de-DE" : "en-US";

  return new Intl.NumberFormat(locale, {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 20,
    ...options,
  }).format(number);
};

/** Formats dynamic measurement fields while preserving identifiers and coded values. */
export const formatFieldValue = (value: unknown, field: string, language = "en"): string => {
  if (value === null || value === undefined || value === "") return "--";
  if (typeof value === "object") return JSON.stringify(value);

  const text = String(value);
  const name = field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9/]+/g, " ");
  // Check field meaning before converting numeric strings: IDs can contain leading zeros
  // or exceed JavaScript's safe integer range.
  const identifier = /\b(?:ids?|identifier|identification|imei\d*|imsi\d*|iccid\d*|ccid\d*|vin|serial|s\/n|uuid|guid|code|pin|phone|mobile|msisdn|ip|mac|plate|targa|matricola|serie|seriennummer|chassis|frame|timestamp|epoch|date|time|packet|payload|hex|binary|bitmask|name|label|type|format|source|operator|forward)\b/;
  if (identifier.test(name)) return text;

  if (typeof value === "number") return formatNumber(value, language);
  if (typeof value !== "string" || !/^[+-]?\d+(?:[.,]\d+)?$/.test(value.trim())) return text;
  return formatNumber(value, language);
};
